import { useState } from 'react';
import useSearchParams from 'hooks/ui/useSearchParams';
import FormWrapper from './FormWrapper';
import { createLpCompanyEditRoute } from 'adminConstants/routes';
import { RevaluationType } from 'further-types/lp/revaluation';
import { CurrencyCode } from 'further-types/lp/vehicle';
import SuccessPage from 'components/new/SuccessPage';
import ReviewRevaluation from './ReviewRevaluation';
import { useRevaluationForm } from './hooks/useRevaluationForm';
import { Revaluation, InvestmentHistory } from './types';
import { AssetFullHistoryItem } from './FormWrapper';
import { HoldingClassInvestmentTotal } from './CreateRevaluation/types';
import { Vehicle } from 'further-types/lp/vehicle';
import Page from 'components/new/Page';
import Stepper from 'components/new/Stepper';
import { Steps, stepLabels } from './steps';
import CreateRevaluationStep from './CreateRevaluation/CreateRevaluationStep';

type Asset = {
  name: string;
  baseCurrency: CurrencyCode;
};

type RevaluationsFormContainerProps = {
  id: string;
  vehicle: Vehicle;
  asset: Asset;
  revaluations: Array<Omit<Revaluation, 'isNew'>>;
  investmentHistory: InvestmentHistory;
  assetFullHistory: Record<string, Array<AssetFullHistoryItem>>;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
};

const RevaluationsFormContainer: React.FC<RevaluationsFormContainerProps> = ({
  id,
  vehicle,
  asset,
  revaluations,
  investmentHistory,
  assetFullHistory,
  holdingClassInvestmentTotals,
}) => {
  const { getNumericParam, setSearchParam } = useSearchParams();
  const rawStep = getNumericParam('step', {
    min: Steps.Create,
    max: Steps.Success,
    defaultValue: Steps.Create,
  });

  const [maxStepReached, setMaxStepReached] = useState<Steps>(Steps.Create);

  const setActiveStep = (step: Steps) => {
    if (step > maxStepReached) setMaxStepReached(step);
    setSearchParam('step', String(step));
  };

  // Build newRevaluations from holdingClassInvestmentTotals (all holding classes)
  const newRevaluations = holdingClassInvestmentTotals.map(
    (holdingClassInvestmentTotal) => {
      const holdingClassId = holdingClassInvestmentTotal.holdingClassId;
      const holdingClassHistory = assetFullHistory[holdingClassId] ?? [];

      return {
        id: holdingClassId,
        revaluationId: undefined,
        holdingClassId,
        type: RevaluationType.Revaluation as const,
        assetId: id,
        holdingClassName: holdingClassHistory[0]?.holdingClassName ?? '',
        unitPriceTcy: holdingClassInvestmentTotal.latestUnitPrice ?? 0,
        currentHoldValue: holdingClassInvestmentTotal.currentHoldValue ?? 0,
        totalNumberOfUnits: holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        currentNumberOfUnits:
          holdingClassInvestmentTotal.totalNumberOfUnits ?? 0,
        ledgerDate: new Date(),
        currency:
          holdingClassInvestmentTotal.currency ??
          asset.baseCurrency ??
          CurrencyCode.GBP,
        isRemoved: false,
        isNew: true,
      };
    },
  );

  const formMethods = useRevaluationForm({
    investmentHistory,
    revaluations,
    asset,
    newRevaluations,
    holdingClassInvestmentTotals,
  });

  const activeStep = Math.min(rawStep, maxStepReached) as Steps;

  const heading = `Revalue ${asset.name}`;
  const goBackAction = createLpCompanyEditRoute(id);

  if (activeStep === Steps.Success) {
    return (
      <SuccessPage
        title="Revaluation has been created successfully"
        content="The asset and general ledger have been updated and this new revaluation will feed into data across the system."
        primaryButton={{
          label: 'Return to asset',
          link: goBackAction,
        }}
        secondaryButton={{
          label: 'View all assets',
          link: '/assets',
        }}
      />
    );
  }

  return (
    <Page
      spacing="lg"
      tabTitle={heading}
      withStickyHeader
      withHeadingDivider
      headerBreakpoint="md"
      heading={heading}
      goBackAction={goBackAction}
      customActions={
        <Stepper
          activeOnlyBreakpoint="xs"
          maxWidth="md"
          steps={Object.values(stepLabels)}
          activeStep={activeStep}
        />
      }
    >
      <FormWrapper
        formMethods={formMethods}
        externalState={{
          selectedVehicle: vehicle,
          assetFullHistory,
        }}
      >
        {activeStep === Steps.Create && (
          <CreateRevaluationStep
            goBackAction={goBackAction}
            assetName={asset.name}
            assetCurrency={asset.baseCurrency}
            assetId={id}
            vehicleId={vehicle._id}
            vehicle={vehicle}
            holdingClassInvestmentTotals={holdingClassInvestmentTotals}
            onSuccess={() => setActiveStep(Steps.Review)}
          />
        )}

        {activeStep === Steps.Review && (
          <ReviewRevaluation
            handlePreviousStep={() => setActiveStep(Steps.Create)}
            handleSubmit={() => setActiveStep(Steps.Success)}
            vehicle={vehicle}
            assetName={asset.name}
            assetId={id}
          />
        )}
      </FormWrapper>
    </Page>
  );
};

export default RevaluationsFormContainer;
